import { Link } from 'react-router-dom';
import { useApi } from '../lib/hooks.js';

/**
 * Privacy policy and terms.
 *
 * Public: main.jsx routes these outside the sign-in gate, because the people
 * who most need to read them — someone deciding whether to sign up, an app
 * store reviewer, an affiliate network checking a traffic source — have no
 * account and are not going to make one to find out what happens to their data.
 *
 * These describe what the app does *today*, which is why the advertising
 * section is driven by /api/health rather than written as a promise. A policy
 * saying "we show ads" while none are configured is as wrong as one saying the
 * opposite, and the deployment is the only thing that knows which.
 *
 * The rule that made that necessary: each capability obliges an edit here
 * BEFORE it ships. Billing and grocery referral both shipped ahead of theirs,
 * and this is that edit arriving late.
 */

const UPDATED = '8 September 2026';
const CONTACT = 'privacy@pantry-pro.app';

export function Privacy({ ssrAds, ssrInbound }) {
  // Public endpoint, so this works signed out, which is the state most people
  // reading a privacy policy are in. At build time there is no endpoint, so the
  // prerender passes the answers in (see web/src/entry-server.jsx).
  const health = useApi('/health', { skip: ssrAds !== undefined });
  const ads = ssrAds ?? health.data?.ads === true;
  const inbound = ssrInbound ?? health.data?.inboundEmail === true;

  return (
    <Legal title="Privacy policy">
      <p className="legal-meta">Last updated {UPDATED}</p>

      <p>
        Pantry Pro keeps an inventory of the food in your home. Doing that means holding what
        you tell it and what it reads from the photographs you take. This explains what is
        collected, where it goes, and what it is never used for.
      </p>

      <h2>What is collected</h2>
      <ul>
        <li>
          <strong>Your account.</strong> When you sign in with Google or Apple, we receive the
          email address and display name on that account, and an identifier for it. We never
          receive your password.
        </li>
        <li>
          <strong>What you put in.</strong> Inventory, storage locations, purchases, recipes,
          shopping lists, meal plans, notes — everything the app is for.
        </li>
        <li>
          <strong>Photographs.</strong> Receipts, shelves, and cookbook pages you upload, and
          the data extracted from them.
        </li>
        {inbound && (
          <li>
            <strong>Receipts you email.</strong> Your household has an address of its own for
            forwarding store receipts and order confirmations. The message, its attachments, and
            the data extracted from them. Only what is sent to that address; we never read your
            mailbox.
          </li>
        )}
        <li>
          <strong>Operational records.</strong> Ordinary server logs, and records of photo
          analysis — when it ran, which model read it, and how much was used.
        </li>
      </ul>

      <h2>Photographs are read by an AI model</h2>
      <p>
        This is the part worth knowing. When you photograph a receipt, a shelf, or a recipe,
        the image is sent to <strong>Anthropic&rsquo;s API</strong> to be read, and the text it
        returns is what populates your pantry. Recipe text you paste or import is sent the same
        way{inbound ? ', and so is a receipt you email in, whether it arrives as an attachment or as the text of an order confirmation' : ''}.
        Anthropic processes it to answer that request. Barcode scanning is the exception:
        it is decoded in your browser and the image is never uploaded at all.
      </p>
      <p>
        Photograph what you mean to. A receipt shows what you bought, where, and when — and if
        you paid by card, the last digits are often printed on it.
      </p>

      <h2>Who else is involved</h2>
      <ul>
        <li><strong>Supabase</strong> — the database that stores your data, the file storage that holds your photographs, and the service that signs you in.</li>
        <li><strong>Render</strong> — where the application runs.</li>
        <li><strong>Anthropic</strong> — reads photographs and recipe text, as above.</li>
        <li><strong>USDA FoodData Central</strong> and <strong>Open Food Facts</strong> — looked up for nutrition and product information by item name or barcode. Product photographs come from Open Food Facts, and load straight from their servers when a screen showing them is displayed. Like any image hosted elsewhere, that request goes out from your device rather than from us; it does not tell them who you are here, and it does not carry the page you were on.</li>
        <li><strong>Google and Brave search APIs</strong> — used to find recipes when you search for them. Your search terms are sent; nothing about your pantry is.</li>
        <li><strong>Stripe</strong> — takes payment if you subscribe. Card details go to Stripe directly and are never seen by, or stored on, this service.</li>
        {inbound && (
          <li><strong>Resend</strong> — receives mail sent to your household&rsquo;s receipt address and holds it for 30 days so it can be read; it also delivers sign-in and invitation email. Nothing reaches Resend unless you send it there, and it sees only that message, never your mailbox.</li>
        )}
        <li><strong>Grocery partners</strong> — only if you ask. Choosing to send a shopping list to a store transmits the item names and quantities on that list, and nothing else. It needs your explicit permission first, and you can withdraw it at any time in Settings. Following a plain store link sends nothing about you at all.</li>
        {ads && (
          <li><strong>Google AdSense</strong> — serves the advertising on the free plan. Google receives your IP address and the usual request information, as any site with ads does.</li>
        )}
      </ul>
      <p>
        Recipe pages you import are fetched from the site they are published on, which will see
        that request.
      </p>

      <h2>What does not happen</h2>
      <ul>
        <li>Your personal information is not sold, and it is not shared for cross-context behavioural advertising.</li>
        {ads
          ? <li>There is advertising on the free plan, on the recipe and meal-planning screens only — never on your shopping list, and never while you are buying. Paid plans and trials have none. There is no analytics tracker on this site.</li>
          : <li>There is no advertising in the app, and no advertising or analytics tracker on this site.</li>}
        <li>There are no tracking cookies. Signing in stores a session token in your browser so you stay signed in; that is the only thing kept there.</li>
        <li>Your data is not used to train AI models.</li>
      </ul>

      <h2>Keeping and deleting</h2>
      <p>
        Data is kept while the account exists. Deleting an item, recipe or receipt in the app
        removes it, including the stored photograph.
      </p>
      <p>
        <strong>You can take a copy or delete everything yourself,</strong> under Settings.
        The download is one file containing every row your household holds. Deleting removes
        the household and everything in it, deletes the stored photographs and cancels any
        subscription; it cannot be undone and no copy is kept, so take the download first if
        you want one. Anything else &mdash; a correction, a question about what is held &mdash;
        goes to <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. If you no longer have the app,
        see <Link to="/account/delete">Delete your account</Link>.
      </p>

      <h2>If you are in California</h2>
      <p>
        The CCPA as amended gives you the right to know what is collected, to have it deleted,
        to a portable copy, and not to be discriminated against for asking. Use the address
        above, or use the controls in Settings. A <strong>Global Privacy Control</strong> signal
        from your browser is honoured automatically and stored against your household.{' '}
        {ads
          ? <>Opting out means the advertising you see stops being personalised. It does not
            remove the advertising, and nothing else about the app changes &mdash; charging you,
            in features or in money, for exercising a right is the one thing the law plainly
            forbids.</>
          : <>Personal information is not sold or shared for cross-context behavioural
            advertising, so there is nothing an opt-out has to stop &mdash; but the preference is
            recorded all the same, because a signal honoured only from the day it becomes
            inconvenient is not honoured at all.</>}
      </p>

      <h2>Children</h2>
      <p>
        Pantry Pro is not intended for children under 13 and accounts are not knowingly created
        for them.
      </p>

      <h2>Changes</h2>
      <p>
        Material changes will be reflected here with a new date above. Paid plans and grocery
        referral links are live and described above. Advertising, and receiving receipts by
        email, are described above whenever this deployment offers them.
      </p>

      <h2>Contact</h2>
      <p><a href={`mailto:${CONTACT}`}>{CONTACT}</a></p>
    </Legal>
  );
}

/**
 * How to delete an account, for somebody who cannot open the app.
 *
 * Google Play requires this: a page reachable signed out, named for the app as
 * it is listed on the store, that a person who has already uninstalled can use.
 * Everything on it is also true of the website, so it is not a store formality
 * -- it is the one page that works when the app is gone, which is exactly when
 * somebody wants their data gone too.
 *
 * It repeats what Settings already offers rather than linking to it, because
 * the reader may have no way to reach Settings, and it repeats what the privacy
 * policy says rather than restating it differently: two pages describing the
 * same deletion in two ways is how one of them becomes wrong.
 */
export function DeleteAccount() {
  return (
    <Legal title="Delete your Pantry Pro account">
      <p>
        Pantry Pro is the app listed on Google Play under that name, and the same app at
        pantry-pro.app. There are two ways to delete an account and everything in it: from
        inside the app, which happens immediately, and by email, which is for anybody who has
        already uninstalled.
      </p>

      <h2>From inside the app</h2>
      <p>
        Settings &rarr; <strong>Your data</strong> &rarr; <strong>Delete my account and data</strong>,
        then type DELETE to confirm. It cannot be undone.
      </p>
      <p>It removes:</p>
      <ul>
        <li>the household, and every item, purchase, receipt, recipe, shopping list and meal plan in it;</li>
        <li>the photographs uploaded to it, deleted from the storage that holds them;</li>
        <li>the sign-in records of everybody in that household who is not also in another one;</li>
        <li>any subscription, which is cancelled with Stripe as part of the same action.</li>
      </ul>
      <p>
        A pantry is shared, so deleting it is too: anybody in the household can do this, and it
        deletes the household for everyone in it. To remove only yourself, use <strong>Leave</strong>{' '}
        beside your own name under Settings &rarr; Household &mdash; that ends your access and leaves
        the pantry with the people still in it. The last owner cannot leave; make somebody else an
        owner first, or delete the household.
      </p>
      <p>
        No copy is kept afterwards, so if you want one, take it first: Settings &rarr; Your data
        &rarr; <strong>Download my data</strong> is one file containing everything the household
        holds.
      </p>

      <h2>If you have already uninstalled</h2>
      <p>
        Email <a href={`mailto:${CONTACT}`}>{CONTACT}</a> from the address you signed in with and
        ask for your account to be deleted. That address is what identifies the account, so nothing
        else is needed. The request is carried out by hand and confirmed by reply, so you have
        something in writing saying it happened.
      </p>
      <p>
        A request from a different address cannot be matched to an account, and it will be answered
        with a question rather than a deletion &mdash; deleting the wrong household on a guess is
        the one mistake here that cannot be repaired.
      </p>

      <h2>What is kept</h2>
      <p>
        The <Link to="/privacy">privacy policy</Link>, under &ldquo;Keeping and deleting&rdquo;, is
        the authority on this and it is short: deleting removes the household and everything in it,
        deletes the stored photographs, and keeps no copy. The only thing it describes that is not
        part of a household is ordinary server logs, which are operational records rather than
        anything from your pantry.
      </p>
    </Legal>
  );
}

export function Terms() {
  return (
    <Legal title="Terms of use">
      <p className="legal-meta">Last updated {UPDATED}</p>

      <p>
        These terms cover your use of Pantry Pro. Using it means accepting them.
      </p>

      <h2>The service</h2>
      <p>
        Pantry Pro tracks household food inventory and helps you plan meals and shopping around
        it. It is offered as it is, and it may change.
      </p>

      <h2>Your account</h2>
      <p>
        You sign in through Google or Apple. Keep that account secure — anything done through it
        is treated as done by you. One account is for one household; do not share credentials
        with people you would not give your pantry to.
      </p>

      <h2>Extraction is not verification</h2>
      <p>
        This matters more here than in most software. Receipt reading, shelf recognition,
        nutrition lookups and recipe parsing are automated and are sometimes wrong. Quantities,
        units, dates, prices, ingredients and nutrition figures must be checked before you rely
        on them.
      </p>
      <p>
        <strong>
          Do not rely on this app for allergen, dietary or medical decisions.
        </strong>{' '}
        Ingredient lists and nutrition data come from third-party databases and from reading
        photographs, and neither is a substitute for the label on the package in your hand.
      </p>

      <h2>Your content</h2>
      <p>
        What you put in stays yours. You grant only the permission needed to run the service:
        to store your content, and to send photographs and recipe text for the automated
        processing described in the{' '}
        <Link to="/privacy">privacy policy</Link>. Do not upload anything you have no right to
        upload.
      </p>

      <h2>Recipes from elsewhere</h2>
      <p>
        Imported recipes belong to whoever published them. Pantry Pro stores your copy for your
        own use and links back to the source; it does not grant you rights to republish them.
      </p>

      <h2>Acceptable use</h2>
      <p>
        Do not attempt to break, overload or gain unauthorised access to the service, use it
        to store unlawful material, or drive automated traffic through it.
      </p>

      <h2>Availability and liability</h2>
      <p>
        The service is provided without warranty of any kind. It may be unavailable, and data
        loss is possible — keep your own copy of anything you cannot afford to lose. To the
        fullest extent the law allows, liability for indirect, incidental or consequential loss
        is excluded. Nothing here limits liability that cannot lawfully be limited.
      </p>

      <h2>Ending it</h2>
      <p>
        You may stop using the service and request deletion at any time. Access may be
        suspended for breach of these terms.
      </p>

      <h2>Changes</h2>
      <p>
        These terms may be updated; the date above will change when they are. Continuing to use
        the service after that is acceptance of the update.
      </p>

      <h2>Contact</h2>
      <p><a href={`mailto:${CONTACT}`}>{CONTACT}</a></p>
    </Legal>
  );
}

/** Shared reading layout. Deliberately plain — these are documents. */
function Legal({ title, children }) {
  return (
    <div className="legal">
      <article className="legal-body">
        <h1>{title}</h1>
        {children}
        <hr />
        <nav className="landing-links">
          <Link to="/">Home</Link>
          {/* A plain anchor: the guides are prerendered files rather than client
              routes, so the router has nothing to match and would answer with
              the app's catch-all. */}
          <a href="/guides/">Guides</a>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </nav>
      </article>
    </div>
  );
}
